import React, { createContext, useContext, useEffect, useState } from 'react';
import { authApi, tokenStore, api } from '../lib/api';

const AuthContext = createContext(null);

// Iter 69 — preview-as feature: backup the admin token so they can return.
const PREVIEW_BACKUP_KEY = 'canales_admin_backup_token';
const PREVIEW_USER_KEY = 'canales_admin_backup_user';
const PREVIEW_FLAG_KEY = 'canales_in_preview_mode';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inPreview, setInPreview] = useState(false);

  useEffect(() => {
    try { setInPreview(sessionStorage.getItem(PREVIEW_FLAG_KEY) === '1'); } catch { /* ignore */ }
    const token = tokenStore.get();
    let stored = null;
    try { stored = sessionStorage.getItem('canales_user'); } catch { /* ignore */ }
    if (token && stored) {
      try { setUser(JSON.parse(stored)); }
      catch (e) { console.warn('Stored user JSON parse failed:', e); }
      // optional refresh
      authApi.me().then((u) => {
        setUser(u);
        try { sessionStorage.setItem('canales_user', JSON.stringify(u)); } catch { /* ignore */ }
      }).catch(() => {
        tokenStore.clear();
        setUser(null);
      });
    }
    setLoading(false);
  }, []);

  const login = async (email, password, opts = {}) => {
    if (!email || !password || password.length < 6) {
      return { ok: false, error: 'Credenciales inválidas. La contraseña debe tener al menos 6 caracteres.' };
    }
    try {
      const data = await authApi.login(email, password, opts.legalVersion);
      tokenStore.set(data.access_token);
      try { sessionStorage.setItem('canales_user', JSON.stringify(data.user)); } catch { /* ignore */ }
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      const msg = e?.response?.data?.detail || 'No se pudo iniciar sesión';
      return { ok: false, error: msg };
    }
  };

  const logout = () => {
    setUser(null);
    tokenStore.clear();
    try {
      sessionStorage.removeItem(PREVIEW_BACKUP_KEY);
      sessionStorage.removeItem(PREVIEW_USER_KEY);
      sessionStorage.removeItem(PREVIEW_FLAG_KEY);
    } catch { /* ignore */ }
    setInPreview(false);
  };

  const refresh = async () => {
    try {
      const u = await authApi.me();
      setUser(u);
      try { sessionStorage.setItem('canales_user', JSON.stringify(u)); } catch { /* ignore */ }
      return u;
    } catch (_) { return null; }
  };

  // Iter 69 — Preview as role: admin-only, swaps the active token with a
  // short-lived one for a demo user of the requested role. Backs up the
  // admin token so they can return.
  const previewAs = async (roleType) => {
    if (user?.role_type !== 'admin') {
      return { ok: false, error: 'Solo administradores pueden usar Preview' };
    }
    try {
      // Backup current admin token + user
      const adminToken = tokenStore.get();
      try {
        sessionStorage.setItem(PREVIEW_BACKUP_KEY, adminToken);
        sessionStorage.setItem(PREVIEW_USER_KEY, JSON.stringify(user));
      } catch { /* ignore */ }

      const { data } = await api.post('/auth/preview-as', { role_type: roleType });
      tokenStore.set(data.access_token);
      try {
        sessionStorage.setItem('canales_user', JSON.stringify(data.preview_user));
        sessionStorage.setItem(PREVIEW_FLAG_KEY, '1');
      } catch { /* ignore */ }
      setUser(data.preview_user);
      setInPreview(true);
      return { ok: true, preview_user: data.preview_user, minutes: data.preview_minutes };
    } catch (e) {
      // restore backup on failure
      try {
        const bk = sessionStorage.getItem(PREVIEW_BACKUP_KEY);
        if (bk) tokenStore.set(bk);
        sessionStorage.removeItem(PREVIEW_BACKUP_KEY);
        sessionStorage.removeItem(PREVIEW_USER_KEY);
        sessionStorage.removeItem(PREVIEW_FLAG_KEY);
      } catch { /* ignore */ }
      const msg = e?.response?.data?.detail || 'No se pudo iniciar Preview';
      return { ok: false, error: msg };
    }
  };

  const exitPreview = async () => {
    try {
      const bk = sessionStorage.getItem(PREVIEW_BACKUP_KEY);
      const adminUserJson = sessionStorage.getItem(PREVIEW_USER_KEY);
      if (bk) {
        tokenStore.set(bk);
        if (adminUserJson) {
          const adminUser = JSON.parse(adminUserJson);
          setUser(adminUser);
          sessionStorage.setItem('canales_user', adminUserJson);
        } else {
          await refresh();
        }
        sessionStorage.removeItem(PREVIEW_BACKUP_KEY);
        sessionStorage.removeItem(PREVIEW_USER_KEY);
        sessionStorage.removeItem(PREVIEW_FLAG_KEY);
        setInPreview(false);
        return { ok: true };
      }
      return { ok: false, error: 'No hay sesión de admin para regresar' };
    } catch (e) {
      return { ok: false, error: String(e?.message || e) };
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh, inPreview, previewAs, exitPreview }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
